'use client'

import { MoonIcon, SunIcon } from '@radix-ui/react-icons'
import { ActivitySquare, Atom, ChevronLeft, Cpu, Microscope, PencilRuler, Pyramid, Shield, TrendingUp } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { useTheme } from 'next-themes'
import type * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useAdminRole } from '@/hooks/use-admin-role'
import { useFeatureAccessBatch } from '@/hooks/use-feature-access-batch'
import kratos from '@/utils/ory-client'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

import { NavOnboarding } from './components/nav-onboarding'
import { data } from './constanst'

interface MobileSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const { user: authUser, isLoading: authLoading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const { theme, setTheme } = useTheme()
  const { activeTeamId } = useWorkspace()
  const { hasAccess: hasFeature } = useFeatureAccessBatch([
    'agent_profiles',
    'agent_environment',
    'projects',
    'cloud_agent_ide',
    'cloud_agent_ide_v2',
  ])
  const { isAdmin } = useAdminRole()
  const [section, setSection] = useState<'main' | 'sandbox' | 'managed-agents'>('main')

  // Memoize user data to prevent unnecessary re-renders
  const userData = useMemo(() => {
    return data.user(authUser)
  }, [authUser])

  const filteredNavMain = useMemo(() => {
    return data.navMain
      .filter((item) => {
        if (!item.requiredFeatures || item.requiredFeatures.length === 0) return true
        if (item.url === '/agent/profiles') {
          return hasFeature('agent_profiles') || hasFeature('agent_environment')
        }
        return item.requiredFeatures.every((f) => hasFeature(f))
      })
  }, [hasFeature])

  const pushScoped = (path: string) => {
    router.push(appendWorkspaceScopeToPath(path, activeTeamId))
  }
  const pushWorkspaceSettings = () => {
    if (activeTeamId) {
      router.push(appendWorkspaceScopeToPath(`/teams/${activeTeamId}/settings`, activeTeamId))
      return
    }
    router.push('/settings')
  }
  const handleCreateAgentFromSidebar = () => {
    pushScoped('/agents/home')
    onOpenChange(false)
  }

  const handleNavigation = (path: string) => {
    if (path === '/sandbox') {
      setSection('sandbox')
      return
    }
    if (path === '/agent/profiles') {
      setSection('managed-agents')
      return
    }
    if (path === '/settings') {
      pushWorkspaceSettings()
      onOpenChange(false)
      return
    }
    if (path === '/agents/home') {
      handleCreateAgentFromSidebar()
      return
    }
    pushScoped(path)
    onOpenChange(false)
  }

  // When opening mobile sidebar, reflect current route.
  useEffect(() => {
    if (!open) return
    setSection('main')
  }, [open, pathname])

  // Handle logout using the same logic as desktop
  const handleSignOut = async () => {
    try {
      const { data: logoutFlow } = await kratos.createBrowserLogoutFlow(
        undefined,
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json'
          }
        }
      )

      await kratos.updateLogoutFlow(
        {
          token: logoutFlow.logout_token
        },
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json'
          }
        }
      )

      router.push('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      // Still redirect to login on error
      router.push('/login')
    }
  }

  const renderNavItems = (items: any[], _title?: string) => (
    <div className="space-y-2">
      {items.map((item) => (
        <Button
          key={item.title}
          variant="ghost"
          disabled={item.disabled}
          className={`w-full justify-start h-10 px-3 ${item.disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
          onClick={() => !item.disabled && handleNavigation(item.url)}
        >
          {item.icon && <item.icon className="mr-3 h-4 w-4" />}
          <span className="flex items-center gap-2">
            <span>{item.title}</span>
            {item.badgeLabel ? (
              <span className={[
                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                item.badgeTone === 'blue'
                  ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                  : 'bg-pink-500/15 text-pink-600 dark:text-pink-400'
              ].join(' ')}>
                {item.badgeLabel}
              </span>
            ) : null}
          </span>
        </Button>
      ))}
    </div>
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-80 p-0 [&>button:first-child]:hidden">
        {/* Custom close button to ensure it works */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute right-4 top-4 z-50" 
          onClick={() => onOpenChange(false)}
          aria-label="Close sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </Button>
        
        <ScrollArea className="h-full">
          <div className="flex flex-col h-full">

            {/* Content - Added mt-16 to avoid blocking the close button */}
            <div className="flex-1 p-4 space-y-6 mt-7">

              {/* Navigation Items */}
              <div className="grid overflow-hidden">
                <div
                  className={[
                    'transition-all duration-200 ease-out will-change-transform',
                    section === 'main' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none',
                    'col-start-1 row-start-1'
                  ].join(' ')}
                >
                  {renderNavItems(filteredNavMain, 'Main')}
                  {isAdmin && (
                    <div className="mt-2 space-y-1">
                      <Button
                        variant="ghost"
                        className={`w-full justify-start h-10 px-3 ${String(pathname || '').startsWith('/admin') && !String(pathname || '').startsWith('/admin/dispatch') && !String(pathname || '').startsWith('/admin/kpis') ? 'bg-accent' : ''}`}
                        onClick={() => { router.push('/admin'); onOpenChange(false) }}
                      >
                        <Shield className="mr-3 h-4 w-4" />
                        Admin
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start h-10 px-3 ${String(pathname || '').startsWith('/admin/dispatch') ? 'bg-accent' : ''}`}
                        onClick={() => { router.push('/admin/dispatch'); onOpenChange(false) }}
                      >
                        <ActivitySquare className="mr-3 h-4 w-4" />
                        Dispatch Queue
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start h-10 px-3 ${String(pathname || '').startsWith('/admin/kpis') ? 'bg-accent' : ''}`}
                        onClick={() => { router.push('/admin/kpis'); onOpenChange(false) }}
                      >
                        <TrendingUp className="mr-3 h-4 w-4" />
                        KPIs
                      </Button>
                    </div>
                  )}
                </div>

                <div
                  className={[
                    'transition-all duration-200 ease-out will-change-transform',
                    section === 'sandbox' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none',
                    'col-start-1 row-start-1'
                  ].join(' ')}
                >
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-10 px-3 text-muted-foreground"
                      onClick={() => setSection('main')}
                    >
                      <ChevronLeft className="mr-3 h-4 w-4" />
                      {'< Predict'}
                    </Button>

                    <Button variant="ghost" className="w-full justify-start h-10 px-3" onClick={() => handleNavigation('/fold/new')}>
                      <Atom className="mr-3 h-4 w-4 text-muted-foreground" />
                      Fold
                    </Button>
                    <Button variant="ghost" className="w-full justify-start h-10 px-3" onClick={() => handleNavigation('/design/new')}>
                      <PencilRuler className="mr-3 h-4 w-4 text-muted-foreground" />
                      Design
                    </Button>
                    <Button variant="ghost" className="w-full justify-start h-10 px-3" onClick={() => handleNavigation('/properties/new')}>
                      <Microscope className="mr-3 h-4 w-4 text-muted-foreground" />
                      Properties
                    </Button>
                    {/* Workflows moved to main sidebar */}
                  </div>
                </div>

                <div
                  className={[
                    'transition-all duration-200 ease-out will-change-transform',
                    section === 'managed-agents' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none',
                    'col-start-1 row-start-1'
                  ].join(' ')}
                >
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-10 px-3 text-muted-foreground"
                      onClick={() => setSection('main')}
                    >
                      <ChevronLeft className="mr-3 h-4 w-4" />
                      {'< Agents'}
                    </Button>

                    {hasFeature('agent_profiles') && (
                    <Button variant="ghost" className="w-full justify-start h-10 px-3" onClick={() => { pushScoped('/agent/profiles'); onOpenChange(false) }}>
                      <Pyramid className="mr-3 h-4 w-4 text-muted-foreground" />
                      Agents
                    </Button>
                    )}
                    {hasFeature('agent_environment') && (
                    <Button variant="ghost" className="w-full justify-start h-10 px-3" onClick={() => { pushScoped('/agent/environment'); onOpenChange(false) }}>
                      <Cpu className="mr-3 h-4 w-4 text-muted-foreground" />
                      Environment
                    </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t">
              {authLoading ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-2">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                </div>
              ) : authUser ? (
                <>
                  {/* Onboarding */}
                  <div className="mb-3">
                    <NavOnboarding />
                  </div>

                  {/* User Profile */}
                  <div className="flex items-center gap-3 p-2 mb-4">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                      {userData.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{userData.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{userData.email}</p>
                    </div>
                  </div>

                  {/* User Options */}
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-10 px-3"
                      onClick={() => {
                        pushWorkspaceSettings()
                        onOpenChange(false)
                      }}
                    >
                      <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </Button>

                    <Button
                      variant="ghost"
                      className="w-full justify-start h-10 px-3"
                      onClick={() => {
                        setTheme(theme === 'dark' ? 'light' : 'dark')
                        onOpenChange(false)
                      }}
                    >
                      <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                    </Button>

                    <Separator />

                    <Button
                      variant="ghost"
                      className="w-full justify-start h-10 px-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        handleSignOut()
                        onOpenChange(false)
                      }}
                    >
                      <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Log out
                    </Button>
                  </div>
                </>
              ) : (
                /* Auth Buttons */
                <div className="space-y-2">
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      router.push('/signup')
                      onOpenChange(false)
                    }}
                  >
                    Sign up
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => {
                      router.push('/login')
                      onOpenChange(false)
                    }}
                  >
                    Login
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full" 
                    onClick={() => {
                      setTheme(theme === 'dark' ? 'light' : 'dark')
                      onOpenChange(false)
                    }}
                    aria-label={`Activate ${theme === 'dark' ? 'light' : 'dark'} mode`}
                  >
                    <SunIcon className="h-[1rem] w-[1rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <MoonIcon className="absolute h-[1rem] w-[1rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
